import { describeError, useApi } from '@classgo/app-kit';
import { Button, Card, EmptyState, Modal, Table, useToast } from '@classgo/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { academicApi, listQuery } from '../../academic/api';
import type { Entity, ViewOf } from '../../academic/types';
import { useT } from '../../i18n';
import { localizeIssue } from '../../lib/issues';
import { ConfirmDialog } from '../confirm';
import type { EntityListProps } from './types';

/**
 * The list screen every academic entity shares: loading, empty and error states, create and
 * edit in a modal, soft delete behind a confirmation, and the cache refreshed after each change.
 */
export const EntityList = <E extends Entity>({
  entity,
  query = {},
  title,
  caption,
  createLabel,
  editTitle,
  emptyTitle,
  emptyBody,
  columns,
  renderForm,
  toolbar,
  canDelete = () => true,
  onRowClick,
  alsoInvalidate = [],
  initiallyCreating = false,
  onCreateClosed,
}: EntityListProps<E>) => {
  const t = useT();
  const api = useApi();
  const toast = useToast();
  const queryClient = useQueryClient();
  const list = useQuery(listQuery(api, entity, query));
  const [creating, setCreatingState] = useState(initiallyCreating);
  const setCreating = (open: boolean) => {
    setCreatingState(open);
    if (!open) onCreateClosed?.();
  };
  const [editing, setEditing] = useState<ViewOf[E] | null>(null);
  const [deleting, setDeleting] = useState<ViewOf[E] | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['school', entity] });
    for (const key of alsoInvalidate) {
      await queryClient.invalidateQueries({ queryKey: [...key] });
    }
  };

  const remove = useMutation({
    mutationFn: (row: ViewOf[E]) => academicApi(api).remove(entity, (row as { id: string }).id),
    onSuccess: async () => {
      setDeleting(null);
      setDeleteError(null);
      toast.show({ tone: 'success', title: t('entity.deleted') });
      await refresh();
    },
    onError: (thrown) =>
      setDeleteError(
        thrown instanceof Error && 'code' in thrown
          ? localizeIssue(thrown.message).message
          : describeError(t, thrown),
      ),
  });

  const createButton = <Button onClick={() => setCreating(true)}>{createLabel}</Button>;

  return (
    <>
      <Card title={title} actions={createButton} flush={!toolbar}>
        {toolbar ? <div className="mb-4 flex flex-wrap gap-3">{toolbar}</div> : null}
        {list.isError ? (
          <div className="p-6">
            <EmptyState
              title={t('entity.error.title')}
              description={describeError(t, list.error)}
              action={
                <Button variant="secondary" onClick={() => void list.refetch()}>
                  {t('session.retry')}
                </Button>
              }
            />
          </div>
        ) : (
          <Table<ViewOf[E]>
            caption={caption}
            rows={list.data?.items ?? []}
            rowKey={(row) => (row as { id: string }).id}
            loading={list.isPending}
            onRowClick={onRowClick ?? ((row) => setEditing(row))}
            empty={<EmptyState title={emptyTitle} description={emptyBody} action={createButton} />}
            columns={columns}
          />
        )}
      </Card>
      <Modal open={creating} onClose={() => setCreating(false)} title={createLabel}>
        {creating
          ? renderForm({
              current: null,
              onDone: () => {
                setCreating(false);
                void refresh();
              },
            })
          : null}
      </Modal>
      <Modal open={editing !== null} onClose={() => setEditing(null)} title={editTitle}>
        {editing ? (
          <>
            {renderForm({
              current: editing,
              onDone: () => {
                setEditing(null);
                void refresh();
              },
            })}
            {canDelete(editing) ? (
              <div className="mt-4 border-t border-gray-200 pt-4">
                <Button
                  variant="ghost"
                  className="text-danger-700"
                  onClick={() => {
                    setDeleting(editing);
                    setEditing(null);
                    setDeleteError(null);
                  }}
                >
                  {t('entity.delete')}
                </Button>
              </div>
            ) : null}
          </>
        ) : null}
      </Modal>
      <ConfirmDialog
        open={deleting !== null}
        title={t('entity.delete.title')}
        body={t('entity.delete.body', {
          name: (deleting as { name?: string } | null)?.name ?? '',
        })}
        confirmLabel={t('entity.delete')}
        cancelLabel={t('common.cancel')}
        busy={remove.isPending}
        error={deleteError}
        onConfirm={() => {
          if (deleting) remove.mutate(deleting);
        }}
        onClose={() => setDeleting(null)}
      />
    </>
  );
};
